/**
 * i18n key for a catalog widget type's display label.
 *
 * The wire vocabulary is the backend's `typeCode` — `KPI`, `HEATMAP`,
 * `SPEND_BREAKDOWN` — and the locale files are camelCase, so the mapping lives
 * here once instead of being inlined in every component that renders a type.
 *
 * Lower-cased first, because the locale keys were written against the
 * builder's old lowercase ids and eight of the codes differ only in case.
 *
 * @param {string} type Catalog widget type (`typeCode`).
 * @returns {string} Key under the `dashboards` namespace.
 */
export function widgetTypeLabelKey(type) {
  const camel = String(type ?? '')
    .toLowerCase()
    .replace(/_(\w)/g, (_, c) => c.toUpperCase());
  return `widgetLibrary.types.${camel}`;
}

/**
 * Human label for a type code, for when the catalog is not in hand.
 *
 * `HEATMAP` → `Heatmap`, `SPEND_BREAKDOWN` → `Spend breakdown`. Used as the
 * last resort so a type the locale files do not know still reads as a name
 * rather than as a raw key.
 */
export function humanizeWidgetTypeCode(type) {
  const words = String(type ?? '').toLowerCase().replace(/_/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : '';
}

/**
 * The label to print for a type code: the translation when the locale files
 * have one, the humanized code otherwise.
 *
 * The backend owns this vocabulary now, so it can grow a type the front has
 * never heard of — and that must render as a name, not as
 * `widgetLibrary.types.WHATEVER`.
 */
export function widgetTypeLabel(type, t) {
  const key = widgetTypeLabelKey(type);
  const translated = t(key);
  return translated === key ? humanizeWidgetTypeCode(type) : translated;
}
